import { useRef, useState } from "react";
import { clientApiService } from "../../services/client-api.service";

interface UploadReceiptModalProps {
  invoiceId?: string;
  projectId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const MAX_SIZE_MB = 10;

export function UploadReceiptModal({
  invoiceId,
  projectId,
  onClose,
  onSuccess,
}: UploadReceiptModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [amountDeclared, setAmountDeclared] = useState("");
  const [methodDeclared, setMethodDeclared] = useState("transfer");
  const [referenceDeclared, setReferenceDeclared] = useState("");
  const [comment, setComment] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`El archivo excede ${MAX_SIZE_MB}MB`);
      return;
    }

    const allowed = ["image/png", "image/jpeg", "image/webp", "application/pdf"];
    if (!allowed.includes(f.type)) {
      setError("Solo se permiten imágenes (PNG/JPG/WebP) o PDF");
      return;
    }

    setFile(f);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Selecciona un archivo");
      return;
    }
    if (!amountDeclared || parseFloat(amountDeclared) <= 0) {
      setError("Ingresa el monto del comprobante");
      return;
    }

    setIsUploading(true);
    setError(null);
    setProgress(10);

    try {
      // 1. Presign
      const presigned = await clientApiService.presignReceiptUpload({
        filename: file.name,
        contentType: file.type,
        invoiceId,
      });
      setProgress(30);

      // 2. Subir directo a R2
      const uploadRes = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) {
        throw new Error(`Error al subir archivo (${uploadRes.status})`);
      }
      setProgress(70);

      // 3. Registrar el receipt
      await clientApiService.createReceipt({
        invoiceId,
        projectId: projectId ?? undefined,
        fileUrl: presigned.key,
        fileSize: file.size,
        fileType: file.type,
        comment: comment.trim() || undefined,
        amountDeclared: parseFloat(amountDeclared),
        methodDeclared,
        referenceDeclared: referenceDeclared.trim() || undefined,
      });
      setProgress(100);

      onSuccess();
    } catch (err: any) {
      setError(err?.message || "Error al subir el comprobante");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">Subir comprobante</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {/* Archivo */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Archivo (imagen o PDF)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              onChange={handleFileChange}
              className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium hover:file:bg-slate-200 cursor-pointer"
            />
            {file && (
              <p className="text-xs text-slate-400 mt-1">
                {file.name} ({(file.size / 1024).toFixed(0)} KB)
              </p>
            )}
          </div>

          {/* Monto */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Monto del pago <span className="text-primary">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amountDeclared}
              onChange={(e) => setAmountDeclared(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm"
            />
          </div>

          {/* Método */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Método
            </label>
            <select
              value={methodDeclared}
              onChange={(e) => setMethodDeclared(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm"
            >
              <option value="transfer">Transferencia</option>
              <option value="cash">Efectivo</option>
              <option value="card">Tarjeta</option>
              <option value="check">Cheque</option>
              <option value="other">Otro</option>
            </select>
          </div>

          {/* Referencia */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Referencia (opcional)
            </label>
            <input
              type="text"
              value={referenceDeclared}
              onChange={(e) => setReferenceDeclared(e.target.value)}
              placeholder="Folio, número de operación, etc."
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm"
            />
          </div>

          {/* Comentario */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Comentario (opcional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Nota para el despacho..."
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary outline-none text-sm resize-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {error}
            </div>
          )}

          {isUploading && progress > 0 && (
            <div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 text-center mt-1">
                Subiendo... {progress}%
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={isUploading}
              className="flex-1 py-2.5 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={isUploading || !file || !amountDeclared}
              className="flex-1 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50"
            >
              {isUploading ? "Subiendo..." : "Subir"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}